import { Card, CardContent, Typography, Box, Skeleton, Chip } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { AdminSubscriptionDistribution } from '../../../types/admin';

interface SubscriptionChartProps {
  data: AdminSubscriptionDistribution[];
  loading?: boolean;
}

/**
 * SubscriptionChart - Pie chart showing subscription tier distribution
 *
 * Displays:
 * - Distribution of users across subscription tiers
 * - Count and percentage for each tier
 * - Color-coded segments
 *
 * Features:
 * - Interactive segments with hover effects
 * - Legend with tier counts
 * - Responsive sizing
 * - Custom colors for each tier
 * - Loading state support
 */
export default function SubscriptionChart({ data, loading }: SubscriptionChartProps) {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width="40%" height={32} />
          <Skeleton variant="circular" width={250} height={250} sx={{ mt: 2, mx: 'auto' }} />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Subscription Distribution
          </Typography>
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">No data available</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Define colors for each tier
  const tierColors: Record<string, string> = {
    'Free': '#94a3b8',
    'Pro': '#1976d2',
    'Agent': '#9c27b0',
    'Business': '#ed6c02',
  };

  // Prepare data for pie chart
  const chartData = data.map((item, index) => ({
    id: index,
    value: item.count,
    label: item.tier,
    color: tierColors[item.tier] || '#757575',
  }));

  const totalUsers = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Subscription Distribution
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total active subscriptions: {totalUsers.toLocaleString()}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <PieChart
            series={[
              {
                data: chartData,
                highlightScope: { faded: 'global', highlighted: 'item' },
                faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                innerRadius: 30,
                outerRadius: 100,
                paddingAngle: 2,
                cornerRadius: 4,
                cx: 150,
                cy: 120,
              },
            ]}
            width={400}
            height={250}
            slotProps={{
              legend: {
                hidden: true,
              },
            }}
            sx={{
              '& .MuiChartsLegend-series text': {
                fill: 'text.primary !important',
              },
            }}
          />

          {/* Custom legend with counts and percentages */}
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
            {data.map((item, index) => (
              <Chip
                key={index}
                label={`${item.tier}: ${item.count.toLocaleString()} (${item.percentage}%)`}
                size="small"
                sx={{
                  backgroundColor: tierColors[item.tier] || '#757575',
                  color: '#fff',
                  fontWeight: 500,
                  '& .MuiChip-label': {
                    px: 1.5,
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
